import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

import { API } from "../lib/api";
const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === "true";
const DEMO_USER_KEY = "schumann_demo_user";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (DEMO_MODE) {
      const savedDemoUser = localStorage.getItem(DEMO_USER_KEY);
      setUser(savedDemoUser ? JSON.parse(savedDemoUser) : false);
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const firstAccessValidate = async ({ cpf, birth_date }) => {
    if (DEMO_MODE) {
      return {
        first_access_token: `demo-token-${Date.now()}`,
        name: "Marina Schumann",
        email: "marina.schumann@schumann.com.br",
      };
    }

    const { data } = await axios.post(
      `${API}/auth/first-access/validate`,
      { cpf, birth_date },
      { withCredentials: true }
    );
    return data;
  };

  const firstAccessSetPassword = async ({ first_access_token, password, confirm_password }) => {
    if (DEMO_MODE) {
      return { message: "Senha criada em modo demonstracao." };
    }

    const { data } = await axios.post(
      `${API}/auth/first-access/set-password`,
      { first_access_token, password, confirm_password },
      { withCredentials: true }
    );
    return data;
  };

  const changePassword = async ({ current_password, new_password, confirm_password }) => {
    if (DEMO_MODE) {
      const updatedUser = user ? { ...user, must_change_password: false } : user;
      if (updatedUser) {
        localStorage.setItem(DEMO_USER_KEY, JSON.stringify(updatedUser));
      }
      setUser(updatedUser);
      return { message: "Senha alterada em modo demonstracao." };
    }

    const { data } = await axios.post(
      `${API}/auth/change-password`,
      { current_password, new_password, confirm_password },
      { withCredentials: true }
    );
    await checkAuth();
    return data;
  };

  const demoLogin = (role) => {
    if (!DEMO_MODE) return;

    const profiles = {
      colaborador: {
        name: "Marina Colaboradora",
        email: "marina.colaboradora@schumann.com.br",
        department: "Operações",
      },
      autor_area: {
        name: "Marina Autora",
        email: "marina.autora@schumann.com.br",
        department: "Comunicação",
      },
      gerente: {
        name: "Marina Gerente",
        email: "marina.gerente@schumann.com.br",
        department: "Operacoes",
      },
      administrador_total: {
        name: "Marina Administradora",
        email: "marina.admin@schumann.com.br",
        department: "Administração",
      },
    };

    const demoUser = { ...profiles[role], role, isDemo: true, picture: null, must_change_password: false };
    localStorage.setItem(DEMO_USER_KEY, JSON.stringify(demoUser));
    setUser(demoUser);
    return demoUser;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    if (DEMO_MODE) {
      localStorage.removeItem(DEMO_USER_KEY);
      setUser(false);
      return;
    }

    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const hasRole = (minRole) => {
    if (!user) return false;
    const levels = {
      administrador_total: 7,
      admin: 7,
      autor_area: 6,
      area_manager: 6,
      gerente: 5,
      regional_manager: 5,
      store_manager: 4,
      coordinator: 3,
      colaborador: 1,
      analyst: 2,
      operational: 1
    };
    return (levels[user.role] || 0) >= (levels[minRole] || 0);
  };

  const hasAnyRole = (roles = []) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        demoLogin,
        demoMode: DEMO_MODE,
        loginWithGoogle,
        logout,
        hasRole,
        hasAnyRole,
        firstAccessValidate,
        firstAccessSetPassword,
        changePassword,
        setUser,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
